import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileDown, Plus, Save, Search, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";

import {
  borrarFacturaServicio,
  cobrarFacturaServicio,
  crearFacturaServicio,
  descargarFacturaServicioPdf,
  editarFacturaServicio,
  getFacturasServicios,
  getProveedoresAlta,
  type ConceptoServicio,
  type FacturaServicioItem,
  type ProveedorAlta,
} from "@/lib/api";

const money = (n: number, moneda = "MXN") =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: moneda === "USD" ? "USD" : "MXN",
    minimumFractionDigits: 2,
  }).format(n);

const hoyIso = () => new Date().toISOString().slice(0, 10);

/** Honorarios de consultoría por entregable: no cuelgan de un contrato del MEM;
 *  el receptor sale del registro de proveedores del Portal. El folio del CFDI
 *  lo pone Flamma y aquí se captura después, junto con el cobro. */
export function FacturasServiciosPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["facturas-servicios"], queryFn: getFacturasServicios });
  const facturas = q.data ?? [];
  const [error, setError] = useState<string | null>(null);
  const invalidar = () => qc.invalidateQueries({ queryKey: ["facturas-servicios"] });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Facturas de Servicios</h1>
        <p className="text-sm text-slate-500 mt-1">
          Honorarios de consultoría por entregable. No dependen de un contrato del MEM: el
          receptor se toma del registro de proveedores del Portal y sus datos fiscales se
          copian al emitir. El folio del CFDI lo asigna Flamma y se captura aquí después.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}

      <FormularioServicio onEmitida={invalidar} onError={setError} />

      <TablaServicios
        facturas={facturas}
        cargando={q.isLoading}
        onError={setError}
        onCambio={invalidar}
      />
    </div>
  );
}

// ---------- Alta de la factura ----------------------------------------------

function FormularioServicio({
  onEmitida,
  onError,
}: {
  onEmitida: () => void;
  onError: (m: string | null) => void;
}) {
  const [busca, setBusca] = useState("");
  const provQ = useQuery({
    queryKey: ["proveedores-alta", busca],
    queryFn: () => getProveedoresAlta(busca || undefined),
  });
  const [receptor, setReceptor] = useState<ProveedorAlta | null>(null);
  // Receptor capturado a mano (cuando no está dado de alta en el Portal)
  const [manual, setManual] = useState(false);
  const [rfcM, setRfcM] = useState("");
  const [nombreM, setNombreM] = useState("");

  const [emision, setEmision] = useState(hoyIso());
  const [limite, setLimite] = useState("");
  const [moneda, setMoneda] = useState("MXN");
  const [ivaPct, setIvaPct] = useState(0.16);
  const [notas, setNotas] = useState("");
  const [lineas, setLineas] = useState<ConceptoServicio[]>([
    { descripcion: "", cantidad: 1, precio_unitario: 0 },
  ]);

  const setLinea = (i: number, patch: Partial<ConceptoServicio>) =>
    setLineas((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const quitar = (i: number) => setLineas((prev) => prev.filter((_, j) => j !== i));

  const subtotal = useMemo(
    () => Math.round(lineas.reduce((s, l) => s + l.cantidad * l.precio_unitario, 0) * 100) / 100,
    [lineas],
  );
  const iva = Math.round(subtotal * ivaPct * 100) / 100;
  const total = Math.round((subtotal + iva) * 100) / 100;

  const rfc = manual ? rfcM.trim().toUpperCase() : (receptor?.rfc ?? "");
  const nombre = manual ? nombreM.trim() : (receptor?.razon_social ?? "");
  const listo =
    !!rfc && !!nombre && !!emision && lineas.length > 0 &&
    lineas.every((l) => l.descripcion.trim() && l.cantidad > 0 && l.precio_unitario >= 0);

  const emitir = useMutation({
    mutationFn: () =>
      crearFacturaServicio({
        receptor_rfc: rfc,
        receptor_nombre: nombre,
        receptor_regimen: manual ? null : receptor?.regimen_fiscal ?? null,
        receptor_cp: manual ? null : receptor?.cp ?? null,
        receptor_correo: manual ? null : receptor?.contacto_correo ?? null,
        id_proveedor_alta: manual ? null : receptor?.id ?? null,
        fecha_emision: emision,
        fecha_limite_pago: limite || null,
        moneda,
        iva_pct: ivaPct,
        conceptos: lineas.map((l) => ({
          descripcion: l.descripcion.trim(),
          cantidad: l.cantidad,
          precio_unitario: l.precio_unitario,
        })),
        notas: notas.trim() || null,
      }),
    onSuccess: () => {
      onError(null);
      setLineas([{ descripcion: "", cantidad: 1, precio_unitario: 0 }]);
      setNotas("");
      onEmitida();
    },
    onError: (e) => onError(e instanceof Error ? e.message : "No se pudo emitir la factura."),
  });

  return (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <h2 className="text-sm font-semibold text-slate-800">Nueva factura de honorarios</h2>

      {/* Receptor */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Receptor</span>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={manual}
              onChange={(e) => setManual(e.target.checked)}
              className="w-3.5 h-3.5 accent-blue-600"
            />
            capturar a mano (no está dado de alta en el Portal)
          </label>
        </div>

        {manual ? (
          <div className="flex flex-wrap gap-2">
            <input
              value={rfcM}
              onChange={(e) => setRfcM(e.target.value)}
              placeholder="RFC"
              className="border rounded px-2 py-1.5 text-sm w-44 uppercase"
            />
            <input
              value={nombreM}
              onChange={(e) => setNombreM(e.target.value)}
              placeholder="Razón social"
              className="border rounded px-2 py-1.5 text-sm flex-1 min-w-64"
            />
          </div>
        ) : receptor ? (
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 border rounded px-3 py-2 text-sm">
            <span className="font-medium">{receptor.razon_social}</span>
            <span className="text-slate-500">{receptor.rfc}</span>
            {receptor.regimen_fiscal && (
              <span className="text-xs text-slate-400">{receptor.regimen_fiscal}</span>
            )}
            {receptor.cp && <span className="text-xs text-slate-400">C.P. {receptor.cp}</span>}
            <button
              type="button"
              onClick={() => setReceptor(null)}
              className="ml-auto text-slate-400 hover:text-slate-700"
              title="Elegir otro"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por RFC o razón social…"
                className="border rounded pl-7 pr-2 py-1.5 text-sm w-full max-w-md"
              />
            </div>
            <div className="border rounded divide-y max-h-44 overflow-y-auto max-w-md">
              {provQ.isLoading ? (
                <div className="px-3 py-2 text-xs text-slate-500">Buscando…</div>
              ) : (provQ.data ?? []).length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">
                  Sin altas que coincidan. Marca “capturar a mano”.
                </div>
              ) : (
                (provQ.data ?? []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setReceptor(p)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <div className="font-medium">{p.razon_social ?? "—"}</div>
                    <div className="text-xs text-slate-500">
                      {p.rfc ?? "sin RFC"}
                      {p.regimen_fiscal ? ` · ${p.regimen_fiscal}` : ""}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Entregables */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700">Entregables</span>
          <button
            type="button"
            onClick={() =>
              setLineas((prev) => [...prev, { descripcion: "", cantidad: 1, precio_unitario: 0 }])
            }
            className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-50"
          >
            <Plus className="w-3 h-3" /> Entregable
          </button>
        </div>
        <div className="space-y-1.5">
          {lineas.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <input
                value={l.descripcion}
                onChange={(e) => setLinea(i, { descripcion: e.target.value })}
                placeholder="Descripción del entregable (va tal cual al CFDI)"
                className="border rounded px-2 py-1 text-sm flex-1 min-w-72"
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={l.cantidad}
                onChange={(e) => setLinea(i, { cantidad: Number(e.target.value) })}
                title="Cantidad"
                className="border rounded px-2 py-1 text-sm w-20 text-right"
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={l.precio_unitario}
                onChange={(e) => setLinea(i, { precio_unitario: Number(e.target.value) })}
                title="Valor unitario"
                className="border rounded px-2 py-1 text-sm w-36 text-right"
              />
              <span className="text-sm tabular-nums w-32 text-right text-slate-600">
                {money(l.cantidad * l.precio_unitario, moneda)}
              </span>
              <button
                type="button"
                onClick={() => quitar(i)}
                disabled={lineas.length === 1}
                className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                title="Quitar entregable"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 mt-1">
          Clave SAT 80101500 (consultoría de negocios), unidad E48 (unidad de servicio).
        </div>
      </div>

      {/* Datos del documento */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Fecha de emisión</span>
          <input
            type="date"
            value={emision}
            onChange={(e) => setEmision(e.target.value)}
            className="mt-1 border rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Fecha límite de pago</span>
          <input
            type="date"
            value={limite}
            onChange={(e) => setLimite(e.target.value)}
            className="mt-1 border rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Moneda</span>
          <select
            value={moneda}
            onChange={(e) => setMoneda(e.target.value)}
            className="mt-1 border rounded px-2 py-1.5 text-sm"
          >
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">IVA</span>
          <select
            value={ivaPct}
            onChange={(e) => setIvaPct(Number(e.target.value))}
            className="mt-1 border rounded px-2 py-1.5 text-sm"
          >
            <option value={0.16}>16 %</option>
            <option value={0}>0 % (exportación de servicios)</option>
          </select>
        </label>
        <label className="block flex-1 min-w-64">
          <span className="text-xs font-medium text-slate-600">Notas (opcional)</span>
          <input
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Proyecto, periodo, referencia del contrato de servicios…"
            className="mt-1 border rounded px-2 py-1.5 text-sm w-full"
          />
        </label>
      </div>

      {/* Totales + emitir */}
      <div className="flex flex-wrap items-center gap-4 border-t pt-3">
        <div className="text-sm text-slate-600">
          Subtotal <b className="tabular-nums">{money(subtotal, moneda)}</b> · IVA{" "}
          <b className="tabular-nums">{money(iva, moneda)}</b> · TOTAL{" "}
          <b className="tabular-nums text-emerald-800">{money(total, moneda)}</b>
        </div>
        <button
          type="button"
          onClick={() => emitir.mutate()}
          disabled={!listo || emitir.isPending}
          className="ml-auto inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {emitir.isPending ? "Emitiendo…" : "Emitir factura"}
        </button>
      </div>
    </div>
  );
}

// ---------- Lista ------------------------------------------------------------

type SortKeySvc =
  | "fecha_emision" | "receptor_nombre" | "folio_cfdi" | "subtotal"
  | "total" | "moneda" | "fecha_limite_pago" | "cobro";

function TablaServicios({
  facturas,
  cargando,
  onError,
  onCambio,
}: {
  facturas: FacturaServicioItem[];
  cargando: boolean;
  onError: (m: string | null) => void;
  onCambio: () => void;
}) {
  const [sort, setSort] = useState<{ key: SortKeySvc; dir: "asc" | "desc" } | null>(null);
  const [abierta, setAbierta] = useState<number | null>(null);
  const hoy = hoyIso();

  const ordenadas = useMemo(() => {
    if (!sort) return facturas;
    const txt = (v: string | null | undefined) => (v ?? "").toLowerCase();
    const arr = [...facturas];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "fecha_emision": cmp = a.fecha_emision.localeCompare(b.fecha_emision); break;
        case "receptor_nombre":
          cmp = a.receptor_nombre.localeCompare(b.receptor_nombre, "es"); break;
        case "folio_cfdi": cmp = txt(a.folio_cfdi).localeCompare(txt(b.folio_cfdi), "es", { numeric: true }); break;
        case "subtotal": cmp = a.subtotal - b.subtotal; break;
        case "total": cmp = a.total - b.total; break;
        case "moneda": cmp = a.moneda.localeCompare(b.moneda); break;
        case "fecha_limite_pago":
        case "cobro": {
          const f = (x: FacturaServicioItem) =>
            sort.key === "cobro" ? x.cobrada_en : x.fecha_limite_pago;
          const x = f(a), y = f(b);
          if (!x && !y) cmp = 0;
          else if (!x) return 1;   // sin fecha: al final en ambos sentidos
          else if (!y) return -1;
          else cmp = x.localeCompare(y);
          break;
        }
      }
      if (cmp === 0) cmp = a.id - b.id;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [facturas, sort]);

  const cobrar = useMutation({
    mutationFn: (p: { id: number; fecha: string | null }) =>
      cobrarFacturaServicio(p.id, p.fecha),
    onSuccess: () => { onError(null); onCambio(); },
    onError: (e) => onError(e instanceof Error ? e.message : "No se pudo guardar el cobro."),
  });
  const folio = useMutation({
    mutationFn: (p: { id: number; folio: string }) =>
      editarFacturaServicio(p.id, { folio_cfdi: p.folio }),
    onSuccess: () => { onError(null); onCambio(); },
    onError: (e) => onError(e instanceof Error ? e.message : "No se pudo guardar el folio."),
  });
  const borrar = useMutation({
    mutationFn: (id: number) => borrarFacturaServicio(id),
    onSuccess: () => { onError(null); onCambio(); },
    onError: (e) => onError(e instanceof Error ? e.message : "No se pudo borrar la factura."),
  });

  const descargar = async (id: number) => {
    onError(null);
    try {
      await descargarFacturaServicioPdf(id);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Error al generar el PDF");
    }
  };

  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b bg-slate-50">
        <span className="text-sm font-medium text-slate-700">
          {facturas.length} factura{facturas.length === 1 ? "" : "s"} de servicios
        </span>
      </div>
      {cargando ? (
        <div className="p-4 text-sm text-slate-500">Cargando…</div>
      ) : facturas.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">
          Aún no hay facturas de servicios. Emite la primera con el formulario de arriba.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="px-3 py-2 w-8" />
                <ThSvc label="Emisión" k="fecha_emision" sort={sort} setSort={setSort} />
                <ThSvc label="Receptor" k="receptor_nombre" sort={sort} setSort={setSort} />
                <ThSvc label="Folio CFDI" k="folio_cfdi" sort={sort} setSort={setSort} />
                <ThSvc label="Subtotal" k="subtotal" sort={sort} setSort={setSort} align="right" />
                <ThSvc label="Total" k="total" sort={sort} setSort={setSort} align="right" />
                <ThSvc label="Moneda" k="moneda" sort={sort} setSort={setSort} />
                <ThSvc label="Vence" k="fecha_limite_pago" sort={sort} setSort={setSort} />
                <ThSvc label="Cobro" k="cobro" sort={sort} setSort={setSort} />
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((f) => (
                <>
                  <tr
                    key={f.id}
                    className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => setAbierta(abierta === f.id ? null : f.id)}
                  >
                    <td className="px-3 py-2 text-slate-400">{f.id}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{f.fecha_emision}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{f.receptor_nombre}</div>
                      <div className="text-xs text-slate-400">{f.receptor_rfc}</div>
                    </td>
                    <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                      {f.folio_cfdi ? (
                        <span className="font-medium text-slate-700">{f.folio_cfdi}</span>
                      ) : (
                        <input
                          placeholder="A-000"
                          defaultValue=""
                          onBlur={(e) =>
                            e.target.value.trim() &&
                            folio.mutate({ id: f.id, folio: e.target.value.trim() })
                          }
                          className="border rounded px-1.5 py-0.5 text-xs w-24"
                          title="Captura el folio que asignó Flamma al timbrar"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {money(f.subtotal, f.moneda)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {money(f.total, f.moneda)}
                    </td>
                    <td className="px-3 py-2">{f.moneda}</td>
                    <td className="px-3 py-2 text-xs">
                      {f.fecha_limite_pago ? (
                        <span
                          className={
                            !f.cobrada_en && f.fecha_limite_pago < hoy
                              ? "font-medium text-amber-700"
                              : "text-slate-600"
                          }
                        >
                          {f.fecha_limite_pago}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!f.cobrada_en}
                          onChange={(e) =>
                            cobrar.mutate({ id: f.id, fecha: e.target.checked ? hoy : null })
                          }
                          className="w-3.5 h-3.5 accent-emerald-600"
                        />
                        {f.cobrada_en ? (
                          <span className="text-emerald-700">{f.cobrada_en}</span>
                        ) : (
                          <span className="text-slate-400">pendiente</span>
                        )}
                      </label>
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => descargar(f.id)}
                          title="Descargar el PDF de soporte"
                          className="text-slate-400 hover:text-blue-600"
                        >
                          <FileDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={borrar.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `¿Borrar la factura de ${f.receptor_nombre} por ${money(f.total, f.moneda)}?\n\nEsta acción no se puede deshacer.`,
                              )
                            ) {
                              borrar.mutate(f.id);
                            }
                          }}
                          title={
                            f.folio_cfdi
                              ? "Timbrada en Flamma: quita el folio para poder borrarla"
                              : "Borrar factura"
                          }
                          className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {abierta === f.id && (
                    <tr key={`${f.id}-det`} className="border-b bg-slate-50/60">
                      <td colSpan={10} className="px-6 py-3">
                        <div className="text-xs text-slate-500 mb-1.5">
                          Entregables{f.notas ? ` · ${f.notas}` : ""}
                          {f.creado_por ? ` · emitida por ${f.creado_por}` : ""}
                        </div>
                        <table className="w-full text-xs">
                          <thead className="text-slate-500">
                            <tr className="border-b">
                              <th className="px-2 py-1 text-left font-medium">Descripción</th>
                              <th className="px-2 py-1 text-right font-medium">Cantidad</th>
                              <th className="px-2 py-1 text-right font-medium">Valor unitario</th>
                              <th className="px-2 py-1 text-right font-medium">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {f.conceptos.map((c, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="px-2 py-1">{c.descripcion}</td>
                                <td className="px-2 py-1 text-right tabular-nums">{c.cantidad}</td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  {money(c.precio_unitario, f.moneda)}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums font-medium">
                                  {money(c.importe ?? c.cantidad * c.precio_unitario, f.moneda)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Encabezado clicable: ordena por su clave y alterna asc/desc. */
function ThSvc({ label, k, sort, setSort, align }: {
  label: string;
  k: SortKeySvc;
  sort: { key: SortKeySvc; dir: "asc" | "desc" } | null;
  setSort: (s: { key: SortKeySvc; dir: "asc" | "desc" }) => void;
  align?: "right";
}) {
  const activo = sort?.key === k;
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => setSort({ key: k, dir: activo && sort!.dir === "asc" ? "desc" : "asc" })}
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
          activo ? "text-slate-700 font-medium" : ""
        }`}
      >
        {label}
        <span className="text-[10px]">{activo ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default FacturasServiciosPage;
